import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo } from 'react';

import { useApiClient } from '@@common';

import { ApiClientBase } from '../../../api/api';
import { IApiConfig } from '../../../api/ApiConfig';
import { contextDesignerQueryKeys } from '../ContextDesignerQueryKeys';

export interface ContextArtifactLayoutModel {
	folders: ContextArtifactFolderModel[];
	assignments: ContextArtifactFolderAssignmentModel[];
}

export interface ContextArtifactFolderModel {
	id: string;
	categoryId: string;
	name: string;
	parentFolderId?: string | null;
	order?: number;
}

export interface ContextArtifactFolderAssignmentModel {
	artifactKind: string;
	artifactId: string;
	folderId?: string | null;
}

const emptyLayout: ContextArtifactLayoutModel = { folders: [], assignments: [] };

class ContextArtifactLayoutClient extends ApiClientBase {
	private readonly http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
	private readonly baseUrl: string;

	constructor(configuration: IApiConfig, baseUrl?: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }) {
		super(configuration);
		this.http = http ?? (window as any);
		this.baseUrl = baseUrl ?? '';
	}

	async getArtifactLayout(contextId: string): Promise<ContextArtifactLayoutModel> {
		const response = await this.http.fetch(
			`${this.baseUrl}/Context/${encodeURIComponent(contextId)}/artifact-layout`,
			await this.transformOptions({
				method: 'GET',
				headers: { Accept: 'application/json' },
			}),
		);
		return readJsonResponse<ContextArtifactLayoutModel>(response);
	}

	async updateArtifactLayout(contextId: string, layout: ContextArtifactLayoutModel): Promise<void> {
		const response = await this.http.fetch(
			`${this.baseUrl}/Context/${encodeURIComponent(contextId)}/artifact-layout`,
			await this.transformOptions({
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(layout),
			}),
		);
		if (!response.ok) {
			throw new Error(await response.text());
		}
	}
}

export function useContextArtifactLayout(contextId: string) {
	const client = useApiClient(ContextArtifactLayoutClient);
	const queryClient = useQueryClient();

	const { data: layout = emptyLayout, isLoading, refetch } = useQuery({
		queryKey: contextDesignerQueryKeys.artifactLayout(contextId),
		queryFn: () => client.getArtifactLayout(contextId),
		enabled: !!contextId,
		staleTime: 30000,
		gcTime: 5 * 60 * 1000,
	});

	const saveMutation = useMutation({
		mutationFn: (nextLayout: ContextArtifactLayoutModel) => client.updateArtifactLayout(contextId, normalizeLayout(nextLayout)),
		onMutate: async (nextLayout) => {
			const normalized = normalizeLayout(nextLayout);
			await queryClient.cancelQueries({ queryKey: contextDesignerQueryKeys.artifactLayout(contextId) });
			const previousLayout = queryClient.getQueryData<ContextArtifactLayoutModel>(contextDesignerQueryKeys.artifactLayout(contextId));
			queryClient.setQueryData(contextDesignerQueryKeys.artifactLayout(contextId), normalized);
			return { previousLayout };
		},
		onError: (_error, _nextLayout, context) => {
			if (context?.previousLayout) {
				queryClient.setQueryData(contextDesignerQueryKeys.artifactLayout(contextId), context.previousLayout);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: contextDesignerQueryKeys.artifactLayout(contextId) });
		},
	});

	const saveLayout = useCallback((nextLayout: ContextArtifactLayoutModel) => saveMutation.mutateAsync(nextLayout), [saveMutation]);

	const createFolder = useCallback(
		async (categoryId: string, name: string) => {
			await saveLayout({
				...layout,
				folders: [
					...layout.folders,
					{
						id: crypto.randomUUID(),
						categoryId,
						name,
						order: layout.folders.filter((folder) => folder.categoryId === categoryId).length + 1,
					},
				],
			});
		},
		[layout, saveLayout],
	);

	const renameFolder = useCallback(
		async (folderId: string, name: string) => {
			await saveLayout({
				...layout,
				folders: layout.folders.map((folder) => (folder.id === folderId ? { ...folder, name } : folder)),
			});
		},
		[layout, saveLayout],
	);

	const deleteFolder = useCallback(
		async (folderId: string) => {
			await saveLayout({
				folders: layout.folders.filter((folder) => folder.id !== folderId),
				assignments: layout.assignments.filter((assignment) => assignment.folderId !== folderId),
			});
		},
		[layout, saveLayout],
	);

	const moveArtifact = useCallback(
		async (artifactKind: string, artifactId: string, folderId: string | null) => {
			const assignments = layout.assignments.filter((assignment) => assignment.artifactKind !== artifactKind || assignment.artifactId !== artifactId);
			if (folderId) {
				assignments.push({ artifactKind, artifactId, folderId });
			}
			await saveLayout({ ...layout, assignments });
		},
		[layout, saveLayout],
	);

	const getFolderId = useCallback(
		(artifactKind: string, artifactId: string) =>
			layout.assignments.find((assignment) => assignment.artifactKind === artifactKind && assignment.artifactId === artifactId)?.folderId ?? null,
		[layout.assignments],
	);

	const foldersByCategory = useMemo(
		() =>
			layout.folders.reduce<Record<string, ContextArtifactFolderModel[]>>((groups, folder) => {
				groups[folder.categoryId] = groups[folder.categoryId] ?? [];
				groups[folder.categoryId].push(folder);
				return groups;
			}, {}),
		[layout.folders],
	);

	return {
		layout,
		isLoading,
		fetchLayout: refetch,
		createFolder,
		renameFolder,
		deleteFolder,
		moveArtifact,
		getFolderId,
		foldersByCategory,
	};
}

function normalizeLayout(layout: ContextArtifactLayoutModel | null | undefined): ContextArtifactLayoutModel {
	return {
		folders: layout?.folders ?? [],
		assignments: layout?.assignments ?? [],
	};
}

async function readJsonResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		throw new Error(await response.text());
	}
	const text = await response.text();
	return text ? (JSON.parse(text) as T) : (emptyLayout as T);
}
